/**
 * Ask (backend-node routes/ask.js, docs/ask-design.md): answers from the workspace's own data.
 *
 *   GET  /api/v1/ask/status         whether this workspace has Ask, whether this person may ask,
 *                                   the example questions (by page), the month's spend for admins
 *   GET  /api/v1/ask/threads        the person's threads, newest first (?archived=1 the archived ones)
 *   GET  /api/v1/ask/threads/:id    one thread's questions, their steps and answers
 *   PATCH /api/v1/ask/threads/:id   a thread archived or brought back
 *   POST /api/v1/ask                a question, answered as an event stream (streamAsk)
 */
import { api } from '@/api/client'
import { getCsrfToken } from '@/api/session'

export const ASK_PATH = '/ask'

export interface AskExample {
    page: string | null
    text: string
}

export interface AskStatus {
    enabled: boolean
    may_ask: boolean
    examples: AskExample[]
    budget?: { spent_eur: number; limit_eur: number; questions: number }
}

export interface AskThreadSummary {
    thread_id: string
    title: string
    last_at: string
    questions: number
}

/** One tool call: a skill read, or a query (its purpose, then its rows, views and a one-row result's figures). */
export interface AskStep {
    id: number
    tool: 'query' | 'load_skill'
    state: 'running' | 'done' | 'failed'
    purpose?: string
    name?: string
    title?: string
    query?: number
    rows?: number
    views?: string[]
    figures?: { name: string; value: number }[]
    error?: string
    /** In a finished question's steps only (the log's), never while it runs. */
    sql?: string
}

export type AskColumnFormat = 'text' | 'number' | 'share' | 'percent' | 'change' | 'from_to' | 'range' | 'count' | null
/** What a column is to the reader: the row's name, what to do, a plain figure, why. */
export type AskColumnGroup = 'name' | 'action' | 'figure' | 'reason'
export type AskValue = string | number | boolean | null
/** A cell as the database gave it; a visual column's cell holds the parts it reads from its row. */
export type AskCell = AskValue | { from: AskValue; to: AskValue } | { value: AskValue; low: AskValue; high: AskValue; mark?: AskValue } | { value: AskValue; of: AskValue }
export interface AskColumn {
    key: string
    label: string
    format: AskColumnFormat
    /** None in an answer from before columns had groups. */
    group?: AskColumnGroup
}

export interface AskAnswer {
    kind: 'answer' | 'ask_back' | 'out_of_data'
    headline: string
    figures: string[]
    text: string
    table: { query: number; columns: AskColumn[]; rows: AskCell[][]; more: number } | null
    links: { page: string; url: string; title: string }[]
    options: string[]
}

export interface AskNumberCheck {
    ok: boolean
    checked: number
    unverified: { raw: string; context: string }[]
    repaired?: boolean
}

export type AskStatusCode = 'answered' | 'stopped' | 'truncated' | 'refused' | 'failed' | 'over_budget'

export interface AskThreadItem {
    question_id: number
    question: string
    page: string | null
    lang: string | null
    status: AskStatusCode
    steps: AskStep[]
    answer: AskAnswer | null
    number_check: AskNumberCheck | null
    asked_at: string
    ms: number | null
}

export interface AskThread {
    thread_id: string
    items: AskThreadItem[]
    archived: boolean
}

export type AskStage = 'skill' | 'data' | 'check'

export type AskEvent =
    | { type: 'start'; thread_id: string }
    | { type: 'stage'; stage: AskStage }
    | { type: 'step'; step: AskStep }
    | { type: 'answer'; status: AskStatusCode; answer: AskAnswer | null; number_check: AskNumberCheck | null; steps: AskStep[]; question_id: number | null; thread_id: string; ms: number }
    | { type: 'done' }
    | { type: 'error'; code: string }

export async function fetchAskStatus(lang: string, signal?: AbortSignal): Promise<AskStatus> {
    const res = await api.get<{ success: boolean; data: AskStatus }>(`/api/v1/ask/status?lang=${encodeURIComponent(lang)}`, { signal })
    return res.data
}

export async function fetchThreads(archived: boolean, signal?: AbortSignal): Promise<AskThreadSummary[]> {
    const res = await api.get<{ success: boolean; data: AskThreadSummary[] }>(`/api/v1/ask/threads${archived ? '?archived=1' : ''}`, { signal })
    return res.data
}

export async function fetchThread(id: string, signal?: AbortSignal): Promise<AskThread> {
    const res = await api.get<{ success: boolean; data: AskThread }>(`/api/v1/ask/threads/${encodeURIComponent(id)}`, { signal })
    return res.data
}

export async function setThreadArchived(id: string, archived: boolean): Promise<void> {
    await api.patch(`/api/v1/ask/threads/${encodeURIComponent(id)}`, { archived })
}

export interface AskRequest {
    question: string
    lang: string
    page: string | null
    state: Record<string, string>
    thread_id: string | null
}

/** Why a question couldn't start: the HTTP refusal's code (not_enabled, forbidden_by_role, not_configured, …). */
export class AskRefused extends Error {
    readonly code: string

    constructor(code: string) {
        super(code)
        this.code = code
    }
}

/**
 * Asks a question and reads its event stream. fetch rather than EventSource: a POST with the
 * question, the status of a refused request readable, and nothing reconnects (and asks again) on
 * its own. The session cookie authenticates it; the CSRF token goes along, as on every write.
 */
export async function streamAsk(request: AskRequest, { signal, onEvent }: { signal?: AbortSignal; onEvent: (event: AskEvent) => void }): Promise<void> {
    const headers: Record<string, string> = { Accept: 'text/event-stream', 'Content-Type': 'application/json' }
    const csrf = getCsrfToken()
    if (csrf) headers['X-CSRF-Token'] = csrf
    const response = await fetch('/api/v1/ask', { method: 'POST', credentials: 'same-origin', headers, body: JSON.stringify(request), signal })
    if (!response.ok || !response.body) {
        const failure = (await response.json().catch(() => null)) as { code?: unknown } | null
        throw new AskRefused(failure && typeof failure.code === 'string' ? failure.code : response.status === 401 ? 'unauthorized' : 'failed')
    }
    const reader = response.body.pipeThrough(new TextDecoderStream()).getReader()
    let buffer = ''
    for (;;) {
        const { value, done } = await reader.read()
        if (done) break
        buffer += value
        let boundary = buffer.indexOf('\n\n')
        while (boundary !== -1) {
            const frame = buffer.slice(0, boundary)
            buffer = buffer.slice(boundary + 2)
            boundary = buffer.indexOf('\n\n')
            const event = /^event: (.*)$/m.exec(frame)?.[1]
            const data = /^data: (.*)$/m.exec(frame)?.[1]
            if (!event || data === undefined) continue
            try {
                onEvent({ ...(JSON.parse(data) as object), type: event } as AskEvent)
            } catch {
                // A malformed frame is skipped, not fatal.
            }
        }
    }
}
